import { useI18n } from "@/contexts/I18nContext";
import { LOCALES, type Locale } from "@/i18n";

/**
 * English or German.
 *
 * A pair of buttons rather than a dropdown: there are two, and a person
 * scanning a top bar for their own language finds the word faster than a
 * control that has to be opened first.
 *
 * The names are written in their own language — `Deutsch`, not `German` —
 * which is the one place in this app where an untranslated string is correct.
 *
 * The choice lives in this browser. Nothing the admin console does is sent to
 * anybody in a language, so there is nothing an account-level preference would
 * make right.
 */

const NATIVE_NAMES: Record<Locale, string> = { en: "English", de: "Deutsch" };

export function LanguageSwitcher() {
  const { locale, setLocale, t } = useI18n();

  /*
    Kept in this browser and nowhere else.

    The back office saves a language to the account because the backend reads it
    when it sends an email or prints a credential slip. Nothing here does, so an
    account-level preference would be a second place to keep correct for no
    benefit anybody could observe.
  */
  const choose = (option: Locale) => setLocale(option);

  return (
    <div className="flex items-center gap-1" role="group" aria-label={t("common.language")}>
      {LOCALES.map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => choose(option)}
          aria-pressed={option === locale}
          lang={option}
          className={`rounded px-2 py-1 text-sm transition-colors ${
            option === locale
              ? "bg-paper-deep font-medium text-ink"
              : "text-ink-muted hover:bg-paper-warm hover:text-ink"
          }`}
        >
          {NATIVE_NAMES[option]}
        </button>
      ))}
    </div>
  );
}
